import useCountdown from '@util/hooks/useCountdown';
import { formatCurrency, formatTimestamp } from '@util/index';
import { BidDocument } from '@models/bid';
import { useAuth } from 'context/AuthContext';
import { ProductDocument } from 'models/product';
import useRealTimeBids from '@util/hooks/auctions/useRealtimeBids';

const Countdown = ({
  startTime,
  endTime,
}: {
  startTime: number;
  endTime: number;
}) => {
  const isStarted = Date.now() > startTime;
  const countdownType = isStarted ? 'end' : 'start';
  const timeRemaining = useCountdown(startTime, endTime, countdownType);

  return (
    <span
      className="text-[2.4rem] font-semibold leading-[3.2rem] text-brand-light-black"
      suppressHydrationWarning
    >
      {timeRemaining.days > 0 && `${timeRemaining.days}d`} {timeRemaining.hours}
      h {timeRemaining.minutes}m {timeRemaining.seconds}s
    </span>
  );
};

export const JamCrownIcon = () => (
  <svg
    width="20"
    height="21"
    viewBox="0 0 20 21"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3.37436 5.33563L6.54519 8.12563L9.37019 4.6473C9.4493 4.55006 9.54932 4.47191 9.66281 4.41869C9.77631 4.36547 9.90035 4.33853 10.0257 4.33989C10.151 4.34125 10.2745 4.37088 10.3868 4.42655C10.4991 4.48223 10.5974 4.56253 10.6744 4.66146L13.3702 8.1248L16.6435 5.2948C16.7721 5.18389 16.9314 5.11489 17.1003 5.09703C17.2691 5.07918 17.4394 5.11331 17.5883 5.19488C17.7372 5.27644 17.8576 5.40154 17.9334 5.55343C18.0093 5.70533 18.0369 5.87676 18.0127 6.0448L16.7627 14.6673H3.26853L1.99936 6.08396C1.97415 5.91499 2.00148 5.74236 2.07765 5.58944C2.15382 5.43652 2.27515 5.3107 2.42521 5.22904C2.57527 5.14737 2.7468 5.1138 2.91657 5.13286C3.08634 5.15193 3.24615 5.22271 3.37436 5.33563ZM3.33353 15.5006H16.6669V16.334C16.6669 16.555 16.5791 16.7669 16.4228 16.9232C16.2665 17.0795 16.0545 17.1673 15.8335 17.1673H4.16686C3.94584 17.1673 3.73388 17.0795 3.5776 16.9232C3.42132 16.7669 3.33353 16.555 3.33353 16.334V15.5006Z"
      fill="url(#paint0_linear_3227_154941)"
    />
    <defs>
      <linearGradient
        id="paint0_linear_3227_154941"
        x1="1.99023"
        y1="4.33984"
        x2="14.5054"
        y2="19.9806"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#C41719" />
        <stop offset="1" stopColor="#C41719" />
      </linearGradient>
    </defs>
  </svg>
);

interface Props {
  product: ProductDocument;
}
export const AuctionStatus = ({ product }: Props) => {
  const isEnded = Date.now() > product.end_time!;
  const isStarted = Date.now() > product.start_time!;
  const { userDoc } = useAuth();
  const { highestBid, usersBids } = useRealTimeBids(product.id, userDoc?.uid);

  if (!product.end_time || !product.start_time) return null;
  const formatUsername = (username: string) => {
    if (userDoc?.username === username) return 'You';
    // take first and last of username and place 5 asterisks in between
    const first = username.slice(0, 1);
    const last = username.slice(-1);
    return `${first}*****${last}`;
  };
  if (isEnded) {
    return (
      <div className="flex w-full flex-col items-center justify-center gap-[0.8rem] rounded-2xl bg-[#E9F4E9] p-[2rem]">
        <span>Auction ended!</span>
        <div className="flex w-full items-center justify-between gap-[0.4rem]">
          <div className="flex w-full justify-center gap-[0.4rem]">
            <span className="text-[1.8rem] font-semibold leading-[2.5rem]">
              This auction sold for
            </span>
            <span className="text-[1.8rem] font-semibold leading-[2.5rem] text-[#228B22]">
              {formatCurrency(highestBid?.bid_price ?? 0)}
            </span>
          </div>
        </div>
      </div>
    );
  }
  if (!isStarted) {
    return (
      <div className="flex flex-col justify-center rounded-2xl bg-[#F4F4F4]">
        <div className="flex w-full flex-col gap-[0.4rem] p-[2rem]">
          <span className="text-[1.5rem] text-brand-light-black">
            Auction starts {formatTimestamp(product.start_time)}
          </span>
          <Countdown
            startTime={product.start_time}
            endTime={product.end_time}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col justify-center rounded-2xl bg-[#F4F4F4]">
      <div className="flex w-full flex-col gap-[0.4rem] p-[2rem]">
        <span className="text-[1.5rem] text-brand-light-black">
          Auction ends {formatTimestamp(product.end_time)}
        </span>
        <Countdown startTime={product.start_time} endTime={product.end_time} />
      </div>
      <div className="w-full border-[1px] border-white"></div>
      <div className="flex w-full items-center justify-between gap-[0.4rem] p-[2rem]">
        {highestBid ? (
          <>
            <div className="flex flex-col gap-[0.4rem]">
              <span>Current highest bidder: </span>
              <div className="flex items-center gap-[0.5rem]">
                <JamCrownIcon />
                <span className="font-medium">
                  {formatUsername(highestBid?.username ?? '')}
                </span>
              </div>
            </div>
            <span className="text-[3.2rem] font-semibold">
              {formatCurrency(highestBid?.bid_price ?? 0)}
            </span>
          </>
        ) : (
          <>
            <div className="flex flex-col gap-[0.4rem]">
              <span>Current highest bidder: </span>
              <div className="flex items-center gap-[0.5rem]">
                <span className="font-medium">Starting Bid</span>
              </div>
            </div>
            <span className="text-[3.2rem] font-semibold">
              {formatCurrency(product.og_price)}
            </span>
          </>
        )}
      </div>
    </div>
  );
};
